import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Alert, Button, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiClose, mdiChevronLeft, mdiChevronRight, mdiPencilOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { useQuery } from "react-query";

import { QuestionEntity } from "src/entities/QuestionEntity";
import CriteriaService, { criteriaQueryKeys } from "src/services/CriteriaService";
import CriteriaGroupService, { criteriaGroupQueryKeys } from "src/services/CriteriaGroupService";
import CriteriaClassificationService, { criteriaClassificationQueryKeys } from "src/services/CriteriaClassificationService";
import CriteriaManifestationService, { criteriaManifestationQueryKeys } from "src/services/CriteriaManifestationService";
import CriteriaRiskService, { criteriaRiskQueryKeys } from "src/services/CriteriaRiskService";
import CriteriaRecomendationService, { criteriaRecomendationQueryKeys } from "src/services/CriteriaRecomendationService";
import CriteriaDomainService, { criteriaDomainQueryKeys } from "src/services/CriteriaDomainService";
import QuestionnaireResponseService from "src/services/QuestionnaireResponseService";
import { CriteriaGroupEntity } from "src/entities/CriteriaGroupEntity";
import { CriteriaClassificationEntity } from "src/entities/CriteriaClassificationEntity";
import { CriteriaManifestationEntity } from "src/entities/CriteriaManifestationEntity";
import { QuestionnaireResponseQuestionFile } from "src/entities/QuestionnaireResponseEntity";
import { DEFAULT_CRITERIA_MANIFESTATION_IMAGE_LIMIT } from "src/config/criteria";
import GroupsStep from "src/components/FormCriteria/steps/GroupsStep";
import ClassificationsStep from "src/components/FormCriteria/steps/ClassificationsStep";
import ManifestationsStep from "src/components/FormCriteria/steps/ManifestationsStep";
import DomainsStep, { DomainSelections } from "src/components/FormCriteria/steps/DomainsStep";
import ImagesStep, { ManifestationImageSelections } from "src/components/FormCriteria/steps/ImagesStep";
import RisksStep, { RiskSelections } from "src/components/FormCriteria/steps/RisksStep";
import RecomendationsStep, { RecomendationSelections } from "src/components/FormCriteria/steps/RecomendationsStep";

const criteriaService = new CriteriaService();
const criteriaGroupService = new CriteriaGroupService();
const criteriaClassificationService = new CriteriaClassificationService();
const criteriaManifestationService = new CriteriaManifestationService();
const criteriaRiskService = new CriteriaRiskService();
const criteriaRecomendationService = new CriteriaRecomendationService();
const criteriaDomainService = new CriteriaDomainService();
const questionnaireResponseService = new QuestionnaireResponseService();

const steps = [
	{ label: "Grupos" },
	{ label: "Classificações" },
	{ label: "Manifestações" },
	{ label: "Domínios" },
	{ label: "Imagens" },
	{ label: "Riscos" },
	{ label: "Recomendações" },
];

const imagesStepIndex = 4;

function normalizeManifestationFiles(files: QuestionnaireResponseQuestionFile[] = []) {
	return [...files]
		.sort((fileA, fileB) => (fileA.order ?? Number.MAX_SAFE_INTEGER) - (fileB.order ?? Number.MAX_SAFE_INTEGER))
		.map((file, index) => ({ ...file, order: file.order ?? index }));
}

type FormCriteriaProps = {
	question: QuestionEntity;
	primaryColor: string;
	onStatusChange?: (hasAnswers: boolean) => void;
	onChange?: (questionResponses: FormCriteriaQuestionResponse[]) => void;
	onWizardActiveChange?: (active: boolean) => void;
	onRequestPreviousQuestion?: () => void;
	initialQuestionResponses?: FormCriteriaQuestionResponse[];
};

type CriteriaDomainAnswer = {
	id?: number;
	domainId: number;
	domainTitle: string;
	domainOptionId: number;
	domainOptionTitle: string;
};

type CriteriaManifestationAnswer = {
	id?: number;
	manifestationId: number;
	manifestationTitle?: string;
	domains?: CriteriaDomainAnswer[];
	risks?: {
		id?: number;
		riskId: number;
		riskTitle?: string;
	}[];
	recomendations?: {
		id?: number;
		recomendationId: number;
		recomendationTitle?: string;
	}[];
	files: QuestionnaireResponseQuestionFile[];
};

type CriteriaAnswer = {
	id: number;
	responseId?: number;
	groups: CriteriaGroupAnswer[];
	classifications: CriteriaClassificationAnswer[];
	manifestations: CriteriaManifestationAnswer[];
};

type CriteriaGroupAnswer = {
	id?: number;
	criteriaGroupId: number;
	criteriaGroupTitle?: string;
};

type CriteriaClassificationAnswer = {
	id?: number;
	criteriaClassificationId: number;
	criteriaClassificationTitle?: string;
};

export type FormCriteriaQuestionResponse = {
	id?: number;
	questionId: number;
	groups: CriteriaGroupAnswer[];
	classifications: CriteriaClassificationAnswer[];
	manifestations: CriteriaManifestationAnswer[];
};

export type FormCriteriaPayload = {
	questionResponses: FormCriteriaQuestionResponse[];
};

export type FormCriteriaHandle = {
	hasAnswers: () => boolean;
	getPayload: () => FormCriteriaPayload;
};

const tooltipDelay = { show: 1000, hide: 0 };

const FormCriteria = forwardRef<FormCriteriaHandle, FormCriteriaProps>(function FormCriteria(
	{ question, primaryColor, onStatusChange, onChange, onWizardActiveChange, onRequestPreviousQuestion, initialQuestionResponses },
	ref
) {
	const [currentStep, setCurrentStep] = useState(0);
	const [selectedGroups, setSelectedGroups] = useState<CriteriaGroupEntity[]>([]);
	const [selectedClassifications, setSelectedClassifications] = useState<CriteriaClassificationEntity[]>([]);
	const [selectedManifestations, setSelectedManifestations] = useState<CriteriaManifestationEntity[]>([]);
	const [domainSelections, setDomainSelections] = useState<DomainSelections>({});
	const [manifestationFiles, setManifestationFiles] = useState<ManifestationImageSelections>({});
	const [riskSelections, setRiskSelections] = useState<RiskSelections>({});
	const [recomendationSelections, setRecomendationSelections] = useState<RecomendationSelections>({});
	const [uploadingImages, setUploadingImages] = useState<Record<string, boolean>>({});
	const [imageUploadError, setImageUploadError] = useState<string | null>(null);
	const [editingAnswerId, setEditingAnswerId] = useState<number | null>(null);
	const [criteriaAnswers, setCriteriaAnswers] = useState<CriteriaAnswer[]>(() =>
		(initialQuestionResponses ?? []).map((questionResponse, index) => ({
			id: Date.now() + index,
			responseId: questionResponse.id,
			groups: questionResponse.groups ?? [],
			classifications: questionResponse.classifications ?? [],
			manifestations: (questionResponse.manifestations ?? []).map((manifestation) => ({
				...manifestation,
				domains: manifestation.domains ?? [],
				risks: manifestation.risks ?? [],
				recomendations: manifestation.recomendations ?? [],
				files: normalizeManifestationFiles(manifestation.files),
			})),
		}))
	);
	const [showWizard, setShowWizard] = useState((initialQuestionResponses?.length ?? 0) === 0);
	const [canAdvance, setCanAdvance] = useState(true);
	const onStatusChangeRef = useRef(onStatusChange);
	const onChangeRef = useRef(onChange);
	const criteriaId = question.criteriaId;
	const id = String(criteriaId);
	const queryOptions = {
		enabled: criteriaId !== undefined,
		refetchOnWindowFocus: false,
	};

	const criteriaQuery = useQuery(
		criteriaQueryKeys.get(id),
		() => criteriaService.get(criteriaId!).then((response) => response.data),
		queryOptions
	);
	
	const groupsQuery = useQuery(
		criteriaGroupQueryKeys.groups(id),
		() => criteriaGroupService.getGroups(criteriaId!).then((response) => response.data.groups),
		queryOptions
	);

	const classificationsQuery = useQuery(
		criteriaClassificationQueryKeys.classifications(id),
		() => criteriaClassificationService.getClassifications(criteriaId!).then((response) => response.data.classifications),
		queryOptions
	);

	const manifestationsQuery = useQuery(
		criteriaManifestationQueryKeys.manifestations(id),
		() => criteriaManifestationService.getManifestations(criteriaId!).then((response) => response.data.manifestations),
		queryOptions
	);

	const risksQuery = useQuery(
		criteriaRiskQueryKeys.risks(id),
		() => criteriaRiskService.getRisks(criteriaId!).then((response) => response.data.risks),
		queryOptions
	);

	const recomendationsQuery = useQuery(
		criteriaRecomendationQueryKeys.recomendations(id),
		() => criteriaRecomendationService.getRecomendations(criteriaId!).then((response) => response.data.recomendations),
		queryOptions
	);

	const domainsQuery = useQuery(
		criteriaDomainQueryKeys.domains(id),
		() => criteriaDomainService.getDomains(criteriaId!).then((response) => response.data),
		queryOptions
	);

	const queries = [criteriaQuery, groupsQuery, classificationsQuery, manifestationsQuery, risksQuery, recomendationsQuery, domainsQuery];
	const isLoading = queries.some((query) => query.isLoading);
	const isError = queries.some((query) => query.isError);

	useEffect(() => {
		onStatusChangeRef.current = onStatusChange;
	}, [onStatusChange]);

	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	useEffect(() => {
		onStatusChangeRef.current?.(criteriaAnswers.length > 0);
	}, [criteriaAnswers.length]);

	useEffect(() => {
		onWizardActiveChange?.(showWizard);
	}, [onWizardActiveChange, showWizard]);

	const getQuestionResponses = useCallback((): FormCriteriaQuestionResponse[] => {
		return criteriaAnswers.map((criteriaAnswer) => ({
			id: criteriaAnswer.responseId,
			questionId: Number(question.id),
			groups: criteriaAnswer.groups,
			classifications: criteriaAnswer.classifications,
			manifestations: criteriaAnswer.manifestations,
		}));
	}, [criteriaAnswers, question.id]);

	useEffect(() => {
		onChangeRef.current?.(getQuestionResponses());
	}, [getQuestionResponses]);

	useImperativeHandle(
		ref,
		() => ({
			hasAnswers: () => criteriaAnswers.length > 0,
			getPayload: () => ({
				questionResponses: getQuestionResponses(),
			}),
		}),
		[criteriaAnswers.length, getQuestionResponses]
	);

	const canReturnToPreviousQuestion = currentStep === 0 && criteriaAnswers.length === 0 && editingAnswerId === null;

	function handlePrevious() {
		if (canReturnToPreviousQuestion) {
			onRequestPreviousQuestion?.();
			return;
		}

		setCanAdvance(false);
		setCurrentStep((step) => Math.max(step - 1, 0));
	}

	function handleNext() {
		if (currentStep === imagesStepIndex) {
			void handleImagesNext();
			return;
		}

		setCanAdvance(false);
		setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
	}

	async function handleImagesNext() {
		const uploaded = await uploadManifestationImages();

		if (!uploaded) {
			return;
		}

		setCanAdvance(false);
		setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
	}

	function resetWizard() {
		setSelectedGroups([]);
		setSelectedClassifications([]);
		setSelectedManifestations([]);
		setDomainSelections({});
		setManifestationFiles({});
		setRiskSelections({});
		setRecomendationSelections({});
		setUploadingImages({});
		setImageUploadError(null);
		setCurrentStep(0);
		setCanAdvance(true);
	}

	function getEditingAnswer() {
		return editingAnswerId === null ? undefined : criteriaAnswers.find((answer) => answer.id === editingAnswerId);
	}

	function getImageUploadKey(manifestationId: number, fileIndex: number) {
		return `${manifestationId}:${fileIndex}`;
	}

	function hasUploadingImages() {
		return Object.values(uploadingImages).some(Boolean);
	}

	async function uploadManifestationImages() {
		setImageUploadError(null);
		const nextFiles: ManifestationImageSelections = Object.fromEntries(
			Object.entries(manifestationFiles).map(([manifestationId, files]) => [Number(manifestationId), [...files]])
		);
		const pendingUploads: {
			manifestationId: number;
			fileIndex: number;
			file: QuestionnaireResponseQuestionFile;
			uploadKey: string;
		}[] = [];

		for (const manifestation of selectedManifestations) {
			const files = nextFiles[manifestation.id] ?? [];

			for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
				const file = files[fileIndex];

				if (file.fileId) {
					continue;
				}

				if (!file.blob) {
					setImageUploadError("Existem imagens sem arquivo válido. Remova ou substitua a imagem para continuar.");
					return false;
				}

				const uploadKey = getImageUploadKey(manifestation.id, fileIndex);
				pendingUploads.push({ manifestationId: manifestation.id, fileIndex, file, uploadKey });
			}
		}

		if (pendingUploads.length === 0) {
			return true;
		}

		setUploadingImages((current) => ({
			...current,
			...Object.fromEntries(pendingUploads.map((upload) => [upload.uploadKey, true])),
		}));

		const results = await Promise.allSettled(
			pendingUploads.map(async (upload) => {
				const formData = new FormData() as any;
				formData.append("file", upload.file.blob);

				const response = await questionnaireResponseService.postQuestionFile(formData);
				return { ...upload, fileId: response.data };
			})
		);

		const failedUpload = results.find((result) => result.status === "rejected");

		results.forEach((result) => {
			if (result.status !== "fulfilled") {
				return;
			}

			const files = nextFiles[result.value.manifestationId] ?? [];
			files[result.value.fileIndex] = { ...files[result.value.fileIndex], fileId: result.value.fileId };
			nextFiles[result.value.manifestationId] = files;
		});

		setManifestationFiles(nextFiles);

		setUploadingImages((current) => ({
			...current,
			...Object.fromEntries(pendingUploads.map((upload) => [upload.uploadKey, false])),
		}));

		if (failedUpload) {
			setImageUploadError("Não foi possível enviar todas as imagens. Tente novamente ou substitua os arquivos com erro.");
			return false;
		}

		return true;
	}

	function buildManifestationResponses(): CriteriaManifestationAnswer[] {
		const editingAnswer = getEditingAnswer();

		return selectedManifestations.map((manifestation) => ({
			id: editingAnswer?.manifestations.find((item) => item.manifestationId === manifestation.id)?.id,
			manifestationId: manifestation.id,
			manifestationTitle: manifestation.title,
			domains: (domainsQuery.data?.domains ?? [])
				.map((domain) => {
					const selectedOptionId = domainSelections[manifestation.id]?.[domain.criteriaDomainId];
					const selectedOption = domain.options.find((option) => option.enabled && option.criteriaDomainOptionId === selectedOptionId);
					const existingDomain = editingAnswer?.manifestations
						.find((item) => item.manifestationId === manifestation.id)
						?.domains?.find((item) => item.domainId === domain.domainId && item.domainOptionId === selectedOption?.domainOptionId);

					if (!selectedOption) {
						return null;
					}

					return {
						id: existingDomain?.id,
						domainId: domain.domainId,
						domainTitle: domain.title,
						domainOptionId: selectedOption.domainOptionId,
						domainOptionTitle: selectedOption.title,
					};
				}).filter((domain) => domain !== null),
			risks: (riskSelections[manifestation.id] ?? []).map((risk) => ({
				id: editingAnswer?.manifestations.find((item) => item.manifestationId === manifestation.id)?.risks?.find((item) => item.riskId === risk.id)?.id,
				riskId: risk.id,
				riskTitle: risk.title,
			})),
			recomendations: (recomendationSelections[manifestation.id] ?? []).map((recomendation) => ({
				id: editingAnswer?.manifestations
					.find((item) => item.manifestationId === manifestation.id)
					?.recomendations?.find((item) => item.recomendationId === recomendation.id)?.id,
				recomendationId: recomendation.id,
				recomendationTitle: recomendation.title,
			})),
			files: normalizeManifestationFiles(
				manifestationFiles[manifestation.id] ?? editingAnswer?.manifestations.find((item) => item.manifestationId === manifestation.id)?.files ?? []
			),
		}));
	}

	function handleComplete() {
		const editingAnswer = getEditingAnswer();
		const answer: CriteriaAnswer = {
			id: editingAnswer?.id ?? Date.now(),
			responseId: editingAnswer?.responseId,
			groups: selectedGroups.map((group) => ({
				id: editingAnswer?.groups.find((item) => item.criteriaGroupId === group.id)?.id,
				criteriaGroupId: group.id,
				criteriaGroupTitle: group.title,
			})),
			classifications: selectedClassifications.map((classification) => ({
				id: editingAnswer?.classifications.find((item) => item.criteriaClassificationId === classification.id)?.id,
				criteriaClassificationId: classification.id,
				criteriaClassificationTitle: classification.title,
			})),
			manifestations: buildManifestationResponses(),
		};
		
		const nextAnswers =
			editingAnswerId === null
				? [...criteriaAnswers, answer]
				: criteriaAnswers.map((criteriaAnswer) => (criteriaAnswer.id === editingAnswerId ? answer : criteriaAnswer));

		setCriteriaAnswers(nextAnswers);
		setEditingAnswerId(null);
		resetWizard();
		setShowWizard(false);
	}

	function handleAddAnotherAnswer() {
		resetWizard();
		setEditingAnswerId(null);
		setShowWizard(true);
	}

	function handleRemoveAnswer(answerId: number) {
		setCriteriaAnswers((answers) => answers.filter((answer) => answer.id !== answerId));
	}

	function handleEditAnswer(answer: CriteriaAnswer) {
		const groups = answer.groups.map((group) => ({
			id: group.criteriaGroupId,
			title: group.criteriaGroupTitle ?? "",
		}));
		const classifications = answer.classifications.map((classification) => ({
			id: classification.criteriaClassificationId,
			title: classification.criteriaClassificationTitle ?? "",
		}));
		const manifestations = answer.manifestations.map((manifestation) => {
			const loadedManifestation = manifestationsQuery.data?.find((item) => item.id === manifestation.manifestationId);

			return (
				loadedManifestation ?? {
					id: manifestation.manifestationId,
					title: manifestation.manifestationTitle ?? "",
					technicalImportance: null,
					functionalImportance: null,
					groups,
					classifications,
				}
			);
		});
		const nextDomainSelections = answer.manifestations.reduce<DomainSelections>((acc, manifestation) => {
			const domainSelectionsByManifestation = (manifestation.domains ?? []).reduce<Record<number, number>>((domainAcc, domainAnswer) => {
				const domain = domainsQuery.data?.domains.find((item) => item.domainId === domainAnswer.domainId);
				const option = domain?.options.find((item) => item.domainOptionId === domainAnswer.domainOptionId);

				if (domain && option) {
					domainAcc[domain.criteriaDomainId] = option.criteriaDomainOptionId;
				}

				return domainAcc;
			}, {});

			acc[manifestation.manifestationId] = domainSelectionsByManifestation;
			return acc;
		}, {});
		const nextRiskSelections = answer.manifestations.reduce<RiskSelections>((acc, manifestation) => {
			acc[manifestation.manifestationId] = (manifestation.risks ?? []).map((risk) => {
				return (
					risksQuery.data?.find((item) => item.id === risk.riskId) ?? {
						id: risk.riskId,
						title: risk.riskTitle ?? "",
						description: "",
						manifestations: [{ id: manifestation.manifestationId, title: manifestation.manifestationTitle ?? "" }],
					}
				);
			});
			return acc;
		}, {});
		const nextRecomendationSelections = answer.manifestations.reduce<RecomendationSelections>((acc, manifestation) => {
			acc[manifestation.manifestationId] = (manifestation.recomendations ?? []).map((recomendation) => {
				return (
					recomendationsQuery.data?.find((item) => item.id === recomendation.recomendationId) ?? {
						id: recomendation.recomendationId,
						title: recomendation.recomendationTitle ?? "",
						description: "",
						manifestations: [{ id: manifestation.manifestationId, title: manifestation.manifestationTitle ?? "" }],
					}
				);
			});
			return acc;
		}, {});
		const nextManifestationFiles = answer.manifestations.reduce<ManifestationImageSelections>((acc, manifestation) => {
			acc[manifestation.manifestationId] = normalizeManifestationFiles(manifestation.files);
			return acc;
		}, {});

		setEditingAnswerId(answer.id);
		setSelectedGroups(groups);
		setSelectedClassifications(classifications);
		setSelectedManifestations(manifestations);
		setDomainSelections(nextDomainSelections);
		setManifestationFiles(nextManifestationFiles);
		setRiskSelections(nextRiskSelections);
		setRecomendationSelections(nextRecomendationSelections);
		setCurrentStep(0);
		setCanAdvance(true);
		setShowWizard(true);
	}

	function handleManifestationsChange(manifestations: CriteriaManifestationEntity[]) {
		const selectedIds = new Set(manifestations.map((manifestation) => manifestation.id));

		const nextDomainSelections = Object.fromEntries(
			Object.entries(domainSelections).filter(([manifestationId]) => selectedIds.has(Number(manifestationId)))
		);

		const nextRiskSelections = Object.fromEntries(
			Object.entries(riskSelections).filter(([manifestationId]) => selectedIds.has(Number(manifestationId)))
		);
		
		const nextRecomendationSelections = Object.fromEntries(
			Object.entries(recomendationSelections).filter(([manifestationId]) => selectedIds.has(Number(manifestationId)))
		);
		const nextManifestationFiles = Object.fromEntries(
			Object.entries(manifestationFiles).filter(([manifestationId]) => selectedIds.has(Number(manifestationId)))
		);

		setSelectedManifestations(manifestations);
		setDomainSelections(nextDomainSelections);
		setManifestationFiles(nextManifestationFiles);
		setRiskSelections(nextRiskSelections);
		setRecomendationSelections(nextRecomendationSelections);
	}

	function renderCurrentStep() {
		switch (currentStep) {
			case 0:
				return (
					<GroupsStep
						options={groupsQuery.data ?? []}
						selectedOptions={selectedGroups}
						onChange={setSelectedGroups}
						onCanAdvanceChange={setCanAdvance}
					/>
				);
			case 1:
				return (
					<ClassificationsStep
						options={classificationsQuery.data ?? []}
						selectedOptions={selectedClassifications}
						onChange={setSelectedClassifications}
						onCanAdvanceChange={setCanAdvance}
					/>
				);
			case 2:
				return (
					<ManifestationsStep
						options={manifestationsQuery.data ?? []}
						selectedGroups={selectedGroups}
						selectedClassifications={selectedClassifications}
						selectedOptions={selectedManifestations}
						onChange={handleManifestationsChange}
						onCanAdvanceChange={setCanAdvance}
					/>
				);
			case 3:
				return (
					<DomainsStep
						manifestations={selectedManifestations}
						domains={domainsQuery.data?.domains ?? []}
						selections={domainSelections}
						onChange={setDomainSelections}
						onCanAdvanceChange={setCanAdvance}
					/>
				);
			case 4:
				return (
					<ImagesStep
						manifestations={selectedManifestations}
						files={manifestationFiles}
						maxImages={DEFAULT_CRITERIA_MANIFESTATION_IMAGE_LIMIT}
						uploadingImages={uploadingImages}
						uploadError={imageUploadError}
						onChange={setManifestationFiles}
						onCanAdvanceChange={setCanAdvance}
					/>
				);
			case 5:
				return (
					<RisksStep
						manifestations={selectedManifestations}
						risks={risksQuery.data ?? []}
						selections={riskSelections}
						onChange={setRiskSelections}
						onCanAdvanceChange={setCanAdvance}
					/>
				);
			case 6:
				return (
					<RecomendationsStep
						manifestations={selectedManifestations}
						recomendations={recomendationsQuery.data ?? []}
						selections={recomendationSelections}
						onChange={setRecomendationSelections}
						onCanAdvanceChange={setCanAdvance}
					/>
				);
			default:
				return null;
		}
	}

	if (isLoading) {
		return (
			<div className="d-flex align-items-center justify-content-center gap-2 py-5 text-muted">
				<Spinner size="sm" />
				Carregando informações do critério
			</div>
		);
	}

	if (isError) {
		return <Alert variant="danger">Não foi possível carregar as informações do critério.</Alert>;
	}

	if (!showWizard && criteriaAnswers.length > 0) {
		return (
			<div className="d-flex flex-column gap-3">
				{criteriaAnswers.map((answer) => {
					const groups = answer.groups.map((group) => group.criteriaGroupTitle).filter(Boolean).join(", ") || "-";
					const classifications = answer.classifications.map((classification) => classification.criteriaClassificationTitle).filter(Boolean).join(", ") || "-";
					const manifestations = answer.manifestations.map((manifestation) => manifestation.manifestationTitle).filter(Boolean).join(", ") || "-";

					return (
						<div key={answer.id} className="d-flex justify-content-between align-items-center gap-3 rounded-3 border p-3">
							<div>
								<div className="small text-muted d-flex flex-column gap-1">
									<div>
										<span className="fw-medium">Grupos:</span> {groups}
									</div>
									<div>
										<span className="fw-medium">Classificações:</span> {classifications}
									</div>
									<div>
										<span className="fw-medium">Manifestações:</span> {manifestations}
									</div>
								</div>
							</div>
							<div className="d-flex align-items-center gap-2 flex-shrink-0">
								<OverlayTrigger delay={tooltipDelay} overlay={<Tooltip>Editar resposta</Tooltip>}>
									<Button
										type="button"
										variant="light"
										className="border rounded-circle p-2 d-flex align-items-center justify-content-center"
										onClick={() => handleEditAnswer(answer)}
										aria-label="Editar resposta"
									>
										<Icon path={mdiPencilOutline} size={0.8} />
									</Button>
								</OverlayTrigger>
								<OverlayTrigger delay={tooltipDelay} overlay={<Tooltip>Remover resposta</Tooltip>}>
									<Button
										type="button"
										variant="light"
										className="border rounded-circle p-2 d-flex align-items-center justify-content-center"
										onClick={() => handleRemoveAnswer(answer.id)}
										aria-label="Remover resposta"
									>
										<Icon path={mdiTrashCanOutline} size={0.8} />
									</Button>
								</OverlayTrigger>
							</div>
						</div>
					);
				})}
				<div className="d-flex justify-content-center">
					<Button type="button" variant="light" className="border rounded-3 px-3 py-2 d-flex align-items-center gap-2" onClick={handleAddAnotherAnswer}>
						<Icon path={mdiPlus} size={0.9} />
						Adicionar outra resposta
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div>
			<div className="criteria-response-stepper mb-4">
				<div className="criteria-response-stepper__track d-flex align-items-start">
				{steps.map((step, index) => {
					const active = index === currentStep;
					const completed = index < currentStep;

					return (
						<div key={step.label} className={`criteria-response-stepper__step d-flex align-items-start ${index === steps.length - 1 ? "criteria-response-stepper__step--last" : ""}`}>
							<div className="criteria-response-stepper__node d-flex flex-column align-items-center text-center">
								<div
									className={`d-flex align-items-center justify-content-center rounded-circle border ${active || completed ? "text-white" : "bg-white text-muted"}`}
									style={{
										width: 32,
										height: 32,
										backgroundColor: active || completed ? primaryColor : undefined,
										borderColor: active || completed ? primaryColor : undefined,
									}}
								>
									{index + 1}
								</div>
								<small className={`mt-1 text-nowrap ${active ? "fw-medium" : completed ? "" : "text-muted"}`} style={{ color: active ? primaryColor : undefined }}>
									{step.label}
								</small>
							</div>
							{index < steps.length - 1 && (
								<div
									className="criteria-response-stepper__connector"
									style={{
										height: 2,
										marginTop: 15,
										backgroundColor: completed ? primaryColor : "#dee2e6",
									}}
								/>
							)}
						</div>
					);
				})}
				</div>
			</div>

			<div className="d-flex align-items-center justify-content-center py-5">{renderCurrentStep()}</div>

			<div className="d-flex justify-content-center gap-2">
				<Button
					type="button"
					variant="light"
					className="border rounded-3 px-3 py-2 d-flex align-items-center gap-2"
					onClick={handlePrevious}
					disabled={currentStep === 0 && !canReturnToPreviousQuestion}
					aria-label="Voltar uma etapa"
				>
					<Icon path={mdiChevronLeft} size={0.85} />
					<span className="small">Anterior</span>
				</Button>
				{currentStep < steps.length - 1 ? (
					<Button
						type="button"
						variant="light"
						className="border rounded-3 px-3 py-2 d-flex align-items-center gap-2"
						onClick={handleNext}
						disabled={!canAdvance || hasUploadingImages()}
						aria-label="Avançar uma etapa"
					>
						<span className="small">Próximo</span>
						<Icon path={mdiChevronRight} size={0.85} />
					</Button>
				) : (
					<Button
						type="button"
						variant="light"
						className="border rounded-3 px-3 py-2 d-flex align-items-center gap-2"
						onClick={handleComplete}
						disabled={!canAdvance || hasUploadingImages()}
						aria-label={editingAnswerId === null ? "Adicionar resposta" : "Atualizar resposta"}
					>
						<span className="small">{editingAnswerId === null ? "Adicionar" : "Atualizar"}</span>
						<Icon path={mdiChevronRight} size={0.85} />
					</Button>
				)}
				{criteriaAnswers.length > 0 && (
					<Button
						type="button"
						variant="light"
						className="border rounded-3 px-3 py-2 d-flex align-items-center gap-2"
						onClick={() => {
							resetWizard();
							setEditingAnswerId(null);
							setShowWizard(false);
						}}
						aria-label="Cancelar resposta"
					>
						<span className="small">Cancelar</span>
						<Icon path={mdiClose} size={0.85} />
					</Button>
				)}
			</div>
		</div>
	);
});

export default FormCriteria;
